import envConfig from '@hulu/env-config';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { FetchResult } from '../../../apis/hooks/useFetch';
import {
  useFetchTargetingCategories,
  useFetchTargetingDimensionsByCategory,
} from '../../../apis/targetingManagerApi/targetingManagerApi';
import type { TargetingValuesResponse } from '../../../apis/targetingManagerApi/targetingManagerApiTypes';
import type { AdFormValues, TargetingValueOption } from '../../AdForm/adFormik';
import type { DropdownOption } from '../../Dropdown';
import type { TargetingScope } from '../../Targeting';
import { GEO_CATEGORY_NAME } from '../constants';
import type { TargetingDropdownOption } from '../types';
import { createDropdownOptionForCategoryOrDimension } from './utils';

// Re-export for backward compatibility
export type { TargetingDropdownOption } from '../types';

export interface UseTargetingDropdownsReturn {
  categoryOptions: DropdownOption[];
  selectedCategory: TargetingDropdownOption | null;
  setSelectedCategory: (category: TargetingDropdownOption | null) => void;
  isLoadingCategories: boolean;
  errorGettingCategories: string | undefined;
  dimensionsOptions: DropdownOption[];
  selectedDimension: TargetingDropdownOption | null;
  setSelectedDimension: (dimension: TargetingDropdownOption | null) => void;
  isLoadingDimensionsByCategory: boolean;
  errorGettingDimensionsByCategory: string | undefined;
  selectedValues: TargetingValueOption[] | undefined;
  setSelectedValues: (values: TargetingValueOption[]) => void;
  clearAll: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

/**
 * Hook for managing targeting dropdown state including categories, dimensions, and values.
 * Handles fetching data, selection state, and provides callbacks for updating selections.
 *
 * @param newTargeting Current Formik `newTargeting` field value (category / dimension / values).
 * @param setNewTargeting Setter for the Formik `newTargeting` field.
 * @param scope Read-only `TargetingScope` describing the entity being targeted. The hook
 *   currently reads `scope.allowEditCountryTarget`; new scope flags consumed by this hook
 *   should be added to `TargetingScope` rather than to the hook's signature.
 */
export default function useTargetingDropdowns(
  newTargeting: AdFormValues['newTargeting'],
  setNewTargeting: (val: AdFormValues['newTargeting']) => void,
  scope: TargetingScope
): UseTargetingDropdownsReturn {
  const allowEditCountryTarget = scope.allowEditCountryTarget ?? false;
  // Use a ref to track the current newTargeting value to avoid dependency loops in callbacks.
  // This prevents infinite re-renders when callbacks that depend on newTargeting are used in effects.
  const newTargetingRef = useRef(newTargeting);
  useEffect(() => {
    newTargetingRef.current = newTargeting;
  }, [newTargeting]);

  const includeTargetingPresets = envConfig.REACT_APP_FEATURE_ENABLE_TARGETING_PRESETS === 'true';
  // Fetch categories using useFetch
  const {
    data: categoriesResponse,
    loading: isLoadingCategories,
    error: errorGettingCategories,
  }: FetchResult<TargetingValuesResponse> = useFetchTargetingCategories(includeTargetingPresets, {});
  // Fetch dimensions
  const {
    data: dimensionsResponse,
    loading: isLoadingDimensionsByCategory,
    error: errorGettingDimensionsByCategory,
  }: FetchResult<TargetingValuesResponse> = useFetchTargetingDimensionsByCategory(newTargeting?.category?.id ?? '', {
    enabled: !!newTargeting?.category?.id,
    allowEditCountryTarget: newTargeting?.category?.label === GEO_CATEGORY_NAME && allowEditCountryTarget,
  });
  const categoryOptions: DropdownOption[] = useMemo(
    () =>
      categoriesResponse?.nodes?.length ? categoriesResponse.nodes.map(createDropdownOptionForCategoryOrDimension) : [],
    [categoriesResponse]
  );
  const dimensionsOptions: DropdownOption[] = useMemo(
    () =>
      dimensionsResponse?.nodes?.length ? dimensionsResponse.nodes.map(createDropdownOptionForCategoryOrDimension) : [],
    [dimensionsResponse]
  );
  const clearAll = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
      e?.preventDefault();
      setNewTargeting({ category: null, dimension: null, values: [] });
    },
    [setNewTargeting]
  );
  const setSelectedCategory = useCallback(
    (category: TargetingDropdownOption | null): void => {
      if (newTargetingRef.current?.category?.value !== category?.value) {
        setNewTargeting({ category, dimension: null, values: [] });
      }
    },
    [setNewTargeting]
  );
  const setSelectedDimension = useCallback(
    (dimension: TargetingDropdownOption | null): void => {
      if (newTargetingRef.current?.dimension?.value !== dimension?.value) {
        setNewTargeting({ ...newTargetingRef.current, dimension, values: [] });
      }
    },
    [setNewTargeting]
  );
  const setSelectedValues = useCallback(
    (values: TargetingValueOption[]): void => {
      setNewTargeting({ ...newTargetingRef.current, values });
    },
    [setNewTargeting]
  );
  return {
    categoryOptions,
    selectedCategory: newTargeting?.category ?? null,
    setSelectedCategory,
    isLoadingCategories,
    errorGettingCategories,
    dimensionsOptions,
    selectedDimension: newTargeting?.dimension ?? null,
    setSelectedDimension,
    isLoadingDimensionsByCategory,
    errorGettingDimensionsByCategory,
    selectedValues: newTargeting?.values,
    setSelectedValues,
    clearAll,
  };
}
